import { db } from "helpers/DBHelper";
import {
  COLLECTIONS,
  STORAGE_KEYS,
  SALE_STATUS,
  CASHBOOK_ENTRY_TYPE,
  PAYMENT_MODES,
  SHIFT_STATUS,
  SHIFT_SYNC_STATUS,
  CASHBOOK_DESCRIPTIONS,
  DRAFT_STATUS,
} from "const";
import { roundOff } from "./NumberFormatHelper";
import ShopService from "services/ShopService";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import * as SalesHelper from "./SalesHelper";
import ShiftService from "services/ShiftService";
import { clearAllIndexedDB } from "./AppSettingsHelper";
import * as SalespersonHelper from "./SalespersonHelper";

export const isPaidAndZeroDue = (sale) => {
  if (!sale) return false;
  return sale.status === SALE_STATUS.PAID && (Number(sale.due) || 0) === 0;
};

export const addShiftPayment = (payment) => {
  return db.add(COLLECTIONS.SHIFT_TRANSACTIONS.name, payment);
};

export const updateShiftPayment = (id, payment) => {
  return db.update(COLLECTIONS.SHIFT_TRANSACTIONS.name, id, payment);
};

export const getShiftPayments = () => {
  return db.getAll(COLLECTIONS.SHIFT_TRANSACTIONS.name);
};

export const filterShiftPayments = (query) => {
  return db.filter(COLLECTIONS.SHIFT_TRANSACTIONS.name, query);
};

export const deleteShiftPaymentsForSale = async (local_id) => {
  const list = await filterShiftPayments({ sale_local_id: local_id });

  for (const sp of list) {
    await db.delete(COLLECTIONS.SHIFT_TRANSACTIONS.name, sp.id);
  }
};

async function getLocalPaymentModeMap() {
  try {
    const modes = (await ShopService.getCachedPaymentModes()) || [];

    const map = {};
    modes.forEach((m) => {
      map[Number(m.id)] = {
        name: m.name,
        order: m.order,
      };
    });
    return map;
  } catch (e) {
    return {};
  }
}

export function buildTransactionFromLocalData(localRows = []) {
  return localRows
    .slice()
    .sort((a, b) => new Date(a.time) - new Date(b.time))
    .map((row) => {
      let description = row.description || "-";
      if (row.type === CASHBOOK_ENTRY_TYPE.EXPENSE) {
        description = `${(row.description || "").replace(/\)(?=[^\s])/g, ") ")}`;
      } else if (row.type === CASHBOOK_ENTRY_TYPE.SALE || row.sale_bill_no || row.sale_local_id) {
        description = `${CASHBOOK_ENTRY_TYPE.SALE}: ${row.sale_bill_no || row.sale_local_id || ""} ${row.description || ""}`;
        if (Number(row.debit) > 0) {
          description = `${description} - ${CASHBOOK_ENTRY_TYPE.BALANCE}`;
        }
      } else if (
        row.type === CASHBOOK_ENTRY_TYPE.CUSTOMER_PAYMENT ||
        description.toUpperCase().includes(CASHBOOK_ENTRY_TYPE.CUSTOMER_PAYMENT)
      ) {
        let notes = row.description || "";
        if (notes.toUpperCase().startsWith(`${CASHBOOK_ENTRY_TYPE.CUSTOMER_PAYMENT}:`)) {
          notes = notes.substring(`${CASHBOOK_ENTRY_TYPE.CUSTOMER_PAYMENT}:`.length).trim();
        }
        description = `${CASHBOOK_ENTRY_TYPE.CUSTOMER_PAYMENT} ${notes ? ": " + notes : ""}`;
        if (row.customer_name && !description.includes(row.customer_name)) {
          description += ` - ${row.customer_name}`;
        }
      } else if (row.type === CASHBOOK_ENTRY_TYPE.PURCHASE || row.type === CASHBOOK_ENTRY_TYPE.VENDOR_PAYMENT) {
        let notes = row.description || "";
        if (notes.toUpperCase().startsWith(`${CASHBOOK_ENTRY_TYPE.PURCHASE}:`)) {
          notes = notes.substring(`${CASHBOOK_ENTRY_TYPE.PURCHASE}:`.length).trim();
        } else if (notes.toUpperCase().startsWith(`${CASHBOOK_ENTRY_TYPE.VENDOR_PAYMENT}:`)) {
          notes = notes.substring(`${CASHBOOK_ENTRY_TYPE.VENDOR_PAYMENT}:`.length).trim();
        }
        description = `${CASHBOOK_ENTRY_TYPE.PURCHASE}${notes ? ": " + notes : ""}`;
        if (row.vendor_name && !description.includes(row.vendor_name)) {
          description += ` - ${row.vendor_name}`;
        }
      }
      return {
        time: row.time,
        credit: roundOff(Number(row.amount) || 0),
        debit: roundOff(Number(row.debit) || 0),
        description,
        reference_id: row.sale_bill_no || row.sale_local_id,
        type: row.type,
        customer_name: row.customer_name,
      };
    });
}

export async function getShiftPaymentsWithPurchases(shiftId) {
  const currentShiftId = Number(shiftId || localStorage.getItem(STORAGE_KEYS.SHIFT_ID));
  return await db.filter(COLLECTIONS.SHIFT_TRANSACTIONS.name, { shift_id: currentShiftId });
}

export async function getLocalShiftTransactions({ shiftId, modeId, paymentMode, shiftOverview, addBalance }) {
  const shiftTransactions = await getShiftPaymentsWithPurchases(shiftId);

  const matchedShiftTransactions = shiftTransactions.filter((shiftTransaction) => {
    const id = shiftTransaction.local_id ?? shiftTransaction.id;
    return id != null && Number(id) === Number(modeId);
  });

  const storedOpening = (matchedShiftTransactions.find((shiftTransaction) => shiftTransaction.opening != null) || {})
    .opening;

  const transactionRows = matchedShiftTransactions.filter(
    (shiftTransaction) => shiftTransaction.type !== CASHBOOK_ENTRY_TYPE.OPENING_BALANCE,
  );

  if (!Array.isArray(transactionRows) || transactionRows.length === 0) {
    if (storedOpening == null) return null;
  }

  const tnx = buildTransactionFromLocalData(transactionRows);

  const openingValue =
    storedOpening != null ? Number(storedOpening) : paymentMode?.opening != null ? Number(paymentMode.opening) : 0;

  const formattedOpening = roundOff(openingValue);

  const openingBalanceEntry = {
    time:
      shiftOverview.opened && shiftOverview.opened !== "-"
        ? shiftOverview.opened
        : matchedShiftTransactions[0]?.time || DateTimeHelpers.getLocalISOString(),
    credit: openingValue,
    debit: 0,
    description: `Opening balance ${formattedOpening}`,
    balance: openingValue,
  };

  const combined = addBalance([openingBalanceEntry, ...tnx]);

  const summary = {
    ...paymentMode,
    name: paymentMode.name,
    opening: openingValue,
    in: tnx.reduce((s, i) => s + (i.credit || 0), 0),
    out: tnx.reduce((s, i) => s + (i.debit || 0), 0),
    closing: openingValue + tnx.reduce((s, i) => s + ((i.credit || 0) - (i.debit || 0)), 0),
  };

  return { summary, tnx: combined };
}

export async function loadShiftHelper(shiftId) {
  try {
    const sales = await SalesHelper.getSalesByShift(shiftId);

    const shiftPaymentsRaw = await getShiftPaymentsWithPurchases(shiftId);

    const paymentModeMap = await getLocalPaymentModeMap();

    const allShifts = JSON.parse(localStorage.getItem(STORAGE_KEYS.ALL_SHIFTS) || "[]");
    const shiftData = allShifts.find((s) => Number(s.id) === Number(shiftId));

    const stockDamages = await getStockDamagesByShift(shiftId);
    const purchases = await getPurchasesByShift(shiftId);
    const activeCartsSummary = await SalesHelper.getActiveCartsSummary(shiftId);

    return processLocalShiftData(
      sales,
      shiftPaymentsRaw,
      paymentModeMap,
      shiftData,
      stockDamages,
      purchases,
      activeCartsSummary,
    );
  } catch (err) {
    console.error("Shift helper error:", err);
    throw err;
  }
}

function buildPaymentModesDescription(sp) {
  let description = sp.description || "-";
  if (sp.type === CASHBOOK_ENTRY_TYPE.EXPENSE) {
    return `${CASHBOOK_ENTRY_TYPE.EXPENSE}: ${(sp.description || "").replace(/\)(?=[^\s])/g, ") ")}`;
  } else if (sp.type === CASHBOOK_ENTRY_TYPE.SALE || sp.sale_bill_no || sp.sale_local_id) {
    description = `${CASHBOOK_ENTRY_TYPE.SALE}: ${sp.sale_bill_no || sp.sale_local_id || ""}: ${sp.description || "-"}`;
    if (Number(sp.debit) > 0) {
      description = `${description} - ${CASHBOOK_ENTRY_TYPE.BALANCE}`;
    }
    return description;
  } else if (
    sp.type === CASHBOOK_ENTRY_TYPE.CUSTOMER_PAYMENT ||
    description.toUpperCase().includes(CASHBOOK_ENTRY_TYPE.CUSTOMER_PAYMENT)
  ) {
    let notes = sp.description || "";
    if (notes.toUpperCase().startsWith(`${CASHBOOK_ENTRY_TYPE.CUSTOMER_PAYMENT}:`)) {
      notes = notes.substring(`${CASHBOOK_ENTRY_TYPE.CUSTOMER_PAYMENT}:`.length).trim();
    }
    description = `${CASHBOOK_ENTRY_TYPE.CUSTOMER_PAYMENT} ${notes ? ": " + notes : ""}`;
    if (sp.customer_name && !description.includes(sp.customer_name)) {
      description += ` - ${sp.customer_name}`;
    }
    return description;
  } else if (
    sp.type === CASHBOOK_ENTRY_TYPE.PURCHASE ||
    sp.type === CASHBOOK_ENTRY_TYPE.VENDOR_PAYMENT ||
    description.toUpperCase().includes("PURCHASE") ||
    description.toUpperCase().includes("VENDOR PAYMENT")
  ) {
    let notes = sp.description || "";
    if (notes.toUpperCase().startsWith(`${CASHBOOK_ENTRY_TYPE.PURCHASE}:`)) {
      notes = notes.substring(`${CASHBOOK_ENTRY_TYPE.PURCHASE}:`.length).trim();
    } else if (notes.toUpperCase().startsWith(`${CASHBOOK_ENTRY_TYPE.VENDOR_PAYMENT}:`)) {
      notes = notes.substring(`${CASHBOOK_ENTRY_TYPE.VENDOR_PAYMENT}:`.length).trim();
    }
    description = `${CASHBOOK_ENTRY_TYPE.PURCHASE}${notes ? ": " + notes : ""}`;
    if (sp.vendor_name && !description.includes(sp.vendor_name)) {
      description += ` - ${sp.vendor_name}`;
    }
    return description;
  }
  return description;
}

function buildPaymentModeSummaries(shiftPaymentsRaw, localModeMap) {
  const paymentModesSummary = {};

  for (const modeId in localModeMap) {
    const mode = localModeMap[modeId];
    paymentModesSummary[modeId] = {
      id: Number(modeId),
      payment: mode.name,
      order: mode.order,
      opening: 0,
      closing: 0,
      in: 0,
      out: 0,
      transactions: [],
    };
  }

  for (const sp of shiftPaymentsRaw) {
    const modeId = sp.local_id ?? sp.id;
    if (modeId == null) continue;
    const mode = localModeMap[modeId];

    if (!paymentModesSummary[modeId]) {
      paymentModesSummary[modeId] = {
        id: Number(modeId) || 0,
        payment: mode?.name,
        order: mode?.order,
        opening: 0,
        closing: 0,
        in: 0,
        out: 0,
        transactions: [],
      };
    }

    paymentModesSummary[modeId].opening = sp.opening != null ? Number(sp.opening) : paymentModesSummary[modeId].opening;
    paymentModesSummary[modeId].in += Number(sp.amount) || 0;
    paymentModesSummary[modeId].out += Number(sp.debit) || 0;

    const description = buildPaymentModesDescription(sp);

    if (sp.type !== CASHBOOK_ENTRY_TYPE.OPENING_BALANCE) {
      paymentModesSummary[modeId].transactions.push({
        time: sp.time,
        credit: Number(sp.amount) || 0,
        debit: sp.debit || 0,
        description,
        reference_id: sp.sale_bill_no || sp.sale_local_id || sp.purchase_id || sp.invoice_no,
        type: sp.type,
        customer_name: sp.customer_name || sp.vendor_name,
      });
    }
  }

  return Object.values(paymentModesSummary)
    .map((paymentMode) => ({
      ...paymentMode,
      closing: (paymentMode.opening || 0) + (paymentMode.in || 0) - (paymentMode.out || 0),
    }))
    .sort((a, b) => a.order - b.order);
}

function buildShiftOrders(sales, localModeMap) {
  const shiftOrders = sales.map((sale) => {
    const paymentModeNameList = Array.isArray(sale.payment_modes)
      ? sale.payment_modes.map((pm) => localModeMap[Number(pm.id)]?.name)
      : [];

    if (sale?.customer_credit_used) {
      paymentModeNameList.push(PAYMENT_MODES.CREDIT);
    }

    const uniqueModes = [...new Set(paymentModeNameList)];
    const paymentModeName = uniqueModes.length > 0 ? uniqueModes.join(", ") : "-";

    return {
      billNo: sale.bill_no,
      bare_bill_no: sale.bare_bill_no || 0,
      local_id: sale.local_id,
      paymentMode: paymentModeName,
      dateTime: sale.time ? DateTimeHelpers.formatDateAndTime(sale.time) : "-",
      name: sale.customer?.name || "-",
      sync_status: sale.sync_status,
      sync_message: sale.sync_message || "",
      type: sale.type || CASHBOOK_ENTRY_TYPE.SALE,
      status: sale.status,
      total: Number(sale.payable) || 0,
    };
  });

  shiftOrders.sort((a, b) => {
    const numA = Number(a.bare_bill_no) || 0;
    const numB = Number(b.bare_bill_no) || 0;
    if (numA && numB && numA !== numB) {
      return numB - numA;
    }
    const strA = String(a.billNo || "");
    const strB = String(b.billNo || "");

    return strB.localeCompare(strA, undefined, { numeric: true });
  });

  return shiftOrders;
}

function calculateShiftTotals(sales, shiftPaymentsRaw, stockDamages, purchases = []) {
  const sumDebits = (type) =>
    shiftPaymentsRaw.reduce(
      (sum, sp) => (sp.type === type ? sum + (Number(sp.debit) || Number(sp.amount) || 0) : sum),
      0,
    );

  const totalBillAmount = sales.reduce((sum, s) => sum + (Number(s.payable) || 0), 0);
  const totalExpenses = sumDebits(CASHBOOK_ENTRY_TYPE.EXPENSE);
  const totalVendorPayments = sumDebits(CASHBOOK_ENTRY_TYPE.VENDOR_PAYMENT) + sumDebits(CASHBOOK_ENTRY_TYPE.PURCHASE);
  const totalPurchaseAmount = calculatePurchaseTotal(purchases);
  const totalOpeningBalance = shiftPaymentsRaw.reduce((sum, sp) => sum + (Number(sp.opening) || 0), 0);
  const totalCredits = sales.reduce((sum, s) => sum + (Number(s.customer_credit_used) || 0), 0);
  const totalCreditReceived = shiftPaymentsRaw.reduce(
    (sum, sp) => (sp.type === CASHBOOK_ENTRY_TYPE.CUSTOMER_PAYMENT ? sum + (Number(sp.amount) || 0) : sum),
    0,
  );
  const totalLoyaltySales = sales.reduce((sum, s) => sum + (Number(s.loyalty_points_amount) || 0), 0);

  const totalStockDamage = calculateStockDamageTotal(stockDamages);
  const closingBalance =
    totalOpeningBalance +
    (totalBillAmount - totalCredits - totalLoyaltySales) +
    totalCreditReceived -
    totalExpenses -
    totalVendorPayments;

  return {
    totalBillAmount,
    totalExpenses,
    totalVendorPayments,
    totalPurchaseAmount,
    totalOpeningBalance,
    totalCredits,
    totalCreditReceived,
    totalLoyaltySales,
    totalStockDamage,
    closingBalance,
  };
}

function processLocalShiftData(
  sales,
  shiftPaymentsRaw,
  paymentModeMap,
  shiftData,
  stockDamages = [],
  purchases = [],
  activeCartsSummary = { activeCartsCount: 0, activeCartsValue: 0 },
) {
  const totals = calculateShiftTotals(sales, shiftPaymentsRaw, stockDamages, purchases);
  const paymentModeSummaries = buildPaymentModeSummaries(shiftPaymentsRaw, paymentModeMap);
  const shiftOrders = buildShiftOrders(sales, paymentModeMap);

  const shiftOverview = {
    name: shiftData?.name || localStorage.getItem(STORAGE_KEYS.SHIFT),
    cashierName: shiftData?.cashier?.name || "-",
    openingBalance: totals.totalOpeningBalance,
    totalDiscount: sales.reduce(
      (sum, s) => sum + (Number(s.discount_applied_on_items) || 0) + (Number(s.discount) || 0),
      0,
    ),
    totalCharges: sales.reduce((sum, s) => sum + (Number(s.totalChargeAmount) || 0), 0),
    opened: shiftData?.opened_at,
    noOfInvoices: sales.length,
    closingBalance: totals.closingBalance,
    totalCredits: totals.totalCredits,
    totalCreditReceived: totals.totalCreditReceived,
    totalLoyaltySales: totals.totalLoyaltySales,
    totalRoundOffAmount: sales.reduce((sum, s) => sum + (Number(s.roundOff) || 0), 0),
    closed: shiftData?.closed_at,
    cloudId: sales[0]?.cloud_id || "---",
    shiftId: shiftData?.id || "---",
    totalBillAmount: totals.totalBillAmount,
    totalExpenses: totals.totalExpenses,
    totalVendorPayments: totals.totalVendorPayments,
    totalPurchaseAmount: totals.totalPurchaseAmount,
    totalStockDamage: totals.totalStockDamage,
    activeCartsCount: activeCartsSummary?.activeCartsCount || 0,
    activeCartsValue: activeCartsSummary?.activeCartsValue || 0,
    status: shiftData?.closed_at ? SHIFT_STATUS.CLOSED : SHIFT_STATUS.OPEN,
  };

  return { shiftOverview, paymentModeSummaries, shiftOrders };
}

export function buildCreateShiftPayload(shiftName, tenders, startTime) {
  const apiTenders = tenders.map((t) => ({
    payment_mode: {
      id: t.payment_mode.id,
      is_active: t.payment_mode.is_enabled,
      name: t.payment_mode.name || "",
      order: t.payment_mode.order,
    },
    opening: Number(t.amount) || 0,
    cash_in: 0,
    cash_out: 0,
    closing: 0,
    actual_closing: 0,
  }));

  const totalOpening = tenders.reduce((sum, t) => sum + (Number(t.amount) || 0), 0);

  const shift = {
    balance: 0,
    createdAt: startTime,
    local_id: null,
    name: shiftName,
    opening: totalOpening,
    totalCreditReceived: 0,
    totalCreditSale: 0,
    totalDiscount: 0,
    totalExpense: 0,
    totalLoyaltySale: 0,
    totalPurchase: 0,
    totalRoundOff: 0,
    totalSale: 0,
    totalStockDamage: 0,
    totalVendorPayment: 0,
    cashier: SalespersonHelper.getCurrentSalesperson(),
  };

  return {
    name: shiftName,
    opened_at: startTime,
    tenders: apiTenders,
    balance: 0,
    shift,
    cashier: SalespersonHelper.getCurrentSalesperson(),
  };
}

export const getShiftName = () => {
  const date = DateTimeHelpers.convertDateToIsoDMY(new Date());
  const savedShifts = JSON.parse(localStorage.getItem(STORAGE_KEYS.ALL_SHIFTS) || "[]");

  const todayShifts = savedShifts.filter((s) => s.date === date);
  const nextShiftNumber = todayShifts.length + 1;

  return `${date}-Shift-${nextShiftNumber}`;
};

export function getCurrentShiftId() {
  const shiftId = localStorage.getItem(STORAGE_KEYS.SHIFT_ID);

  if (!shiftId) {
    return null;
  }

  return {
    id: Number(shiftId),
  };
}

export function isShiftOlderThan24Hours() {
  const shiftObj = getCurrentShiftId();
  if (!shiftObj) return false;

  const allShifts = JSON.parse(localStorage.getItem(STORAGE_KEYS.ALL_SHIFTS) || "[]");
  const shiftData = allShifts.find((s) => Number(s.id) === Number(shiftObj.id));

  if (!shiftData || !shiftData.opened_at) return false;

  const openedAt = DateTimeHelpers.parseLocalISO(shiftData.opened_at);
  const now = new Date();

  const diffInHours = (now - openedAt) / (1000 * 60 * 60);

  return diffInHours >= 24;
}

export function setShiftExpirySkipTime() {
  localStorage.setItem(STORAGE_KEYS.SHIFT_EXPIRY_SKIP_TIME, DateTimeHelpers.getLocalISOString());
}

export function isShiftExpirySkipped() {
  const skipTimeStr = localStorage.getItem(STORAGE_KEYS.SHIFT_EXPIRY_SKIP_TIME);
  if (!skipTimeStr) return false;

  const skipTime = DateTimeHelpers.parseLocalISO(skipTimeStr);
  const now = new Date();
  const diffInHours = (now - skipTime) / (1000 * 60 * 60);

  return diffInHours < 24;
}

export function clearShiftExpirySkipTime() {
  localStorage.removeItem(STORAGE_KEYS.SHIFT_EXPIRY_SKIP_TIME);
}

export async function syncPendingShifts() {
  const allShifts = JSON.parse(localStorage.getItem(STORAGE_KEYS.ALL_SHIFTS) || "[]");
  let updatedShift = false;

  for (let shift of allShifts) {
    if (shift.sync_status === SHIFT_SYNC_STATUS.PENDING_CREATE) {
      try {
        const payload = {
          name: shift.name,
          opened_at: shift.opened_at,
          tenders: shift.tenders || [],
          shift: shift.shift,
        };

        const resp = await ShiftService.createShift(payload);

        const realId = resp.data.id;
        const oldId = shift.id;

        shift.id = realId;

        if (shift.closed_at) {
          shift.sync_status = SHIFT_SYNC_STATUS.PENDING_CLOSE;
        } else {
          shift.sync_status = SHIFT_SYNC_STATUS.SYNCED;
        }

        updatedShift = true;

        const salesToUpdate = await db.filter(COLLECTIONS.SALES.name, { "shift.id": oldId });
        for (let sale of salesToUpdate) {
          sale.shift.id = realId;
          sale.shift.name = shift.name;
          await db.update(COLLECTIONS.SALES.name, sale.local_id, sale);
        }

        const txnsToUpdate = await db.filter(COLLECTIONS.SHIFT_TRANSACTIONS.name, { shift_id: oldId });
        for (let txn of txnsToUpdate) {
          txn.shift_id = realId;
          await db.update(COLLECTIONS.SHIFT_TRANSACTIONS.name, txn.id, txn);
        }

        if (Number(localStorage.getItem(STORAGE_KEYS.SHIFT_ID)) === oldId) {
          localStorage.setItem(STORAGE_KEYS.SHIFT_ID, realId);
        }
      } catch (err) {
        console.error("Failed to sync pending shift create:", err);
      }
    }
  }

  if (updatedShift) {
    localStorage.setItem(STORAGE_KEYS.ALL_SHIFTS, JSON.stringify(allShifts));
  }
}

function buildCloseShiftTenders(paymentModeSummaries) {
  return paymentModeSummaries.map((pm, index) => ({
    cash_in: Number(pm.in) || 0,
    cash_out: Number(pm.out) || 0,
    closing: Number(pm.closing) || 0,
    opening: Number(pm.opening) || 0,
    payment_mode: {
      id: Number(pm.id),
      is_active: true,
      name: pm.payment || "",
      order: index + 1,
    },
  }));
}

function buildCloseShiftDetailes(shiftOverview, shiftId, closedAt) {
  return {
    balance: Number(shiftOverview.closingBalance) || 0,
    closedAt: closedAt,
    id: Number(shiftId),
    createdAt: shiftOverview.opened,
    name: shiftOverview.name,
    noOfInvoice: Number(shiftOverview.noOfInvoices) || 0,
    opening: Number(shiftOverview.openingBalance) || 0,
    status: SHIFT_STATUS.CLOSED,
    totalCreditReceived: Number(shiftOverview.totalCreditReceived) || 0,
    totalCreditSale: Number(shiftOverview.totalCredits) || 0,
    totalDiscount: Number(shiftOverview.totalDiscount) || 0,
    totalExpense: Number(shiftOverview.totalExpenses) || 0,
    totalLoyaltySale: Number(shiftOverview.totalLoyaltySales) || 0,
    totalPurchase: Number(shiftOverview.totalPurchaseAmount) || 0,
    totalRoundOff: Number(shiftOverview.totalRoundOffAmount) || 0,
    totalSale: Number(shiftOverview.totalBillAmount) || 0,
    totalStockDamage: Number(shiftOverview.totalStockDamage) || 0,
    totalVendorPayment: Number(shiftOverview.totalVendorPayments) || 0,
  };
}

export async function buildCloseShiftPayload(shiftId, closedAt) {
  const sales = await SalesHelper.getSalesByShift(shiftId);
  const shiftPaymentsRaw = await getShiftPaymentsWithPurchases(shiftId);
  const paymentModeMap = await getLocalPaymentModeMap();

  const allShifts = JSON.parse(localStorage.getItem(STORAGE_KEYS.ALL_SHIFTS) || "[]");
  const shiftData = allShifts.find((s) => Number(s.id) === Number(shiftId));
  const stockDamages = await getStockDamagesByShift(shiftId);
  const purchases = await getPurchasesByShift(shiftId);

  const { shiftOverview, paymentModeSummaries } = processLocalShiftData(
    sales,
    shiftPaymentsRaw,
    paymentModeMap,
    shiftData,
    stockDamages,
    purchases,
  );

  const tenders = buildCloseShiftTenders(paymentModeSummaries);
  const shiftObj = buildCloseShiftDetailes(shiftOverview, shiftId, closedAt);

  const payload = {
    balance: Number(shiftOverview.closingBalance) || 0,
    closed_at: closedAt,
    opened_at: shiftOverview.opened,
    name: shiftOverview.name,
    shift: shiftObj,
    status: SHIFT_STATUS.CLOSED,
    tenders: tenders,
  };

  return { payload, shiftOverview, paymentModeSummaries, tenders };
}

export async function syncPendingClosedShifts() {
  const allShifts = JSON.parse(localStorage.getItem(STORAGE_KEYS.ALL_SHIFTS) || "[]");
  let updatedShift = false;

  for (let shift of allShifts) {
    if (shift.sync_status === SHIFT_SYNC_STATUS.PENDING_CLOSE) {
      try {
        let closePayload = shift.close_payload;
        if (!closePayload) {
          const res = await buildCloseShiftPayload(shift.id, shift.closed_at || DateTimeHelpers.getLocalISOString());
          closePayload = res.payload;
        }
        await ShiftService.closeShift(shift.id, closePayload);
        shift.sync_status = SHIFT_SYNC_STATUS.SYNCED;
        updatedShift = true;
      } catch (err) {
        console.error("Failed to sync pending shift close:", err);
      }
    }
  }

  if (updatedShift) {
    localStorage.setItem(STORAGE_KEYS.ALL_SHIFTS, JSON.stringify(allShifts));
  }
}

async function logPaymentModeEditsOnly({ diffs, debitModeNames, creditModeNames, sale, shiftId, time }) {
  for (const { pm, diff } of diffs) {
    const isCredit = diff > 0;
    const counterPartyNames = isCredit ? debitModeNames : creditModeNames;
    const description = counterPartyNames
      ? `${isCredit ? CASHBOOK_DESCRIPTIONS.PAYMENT_CHANGE_FROM : CASHBOOK_DESCRIPTIONS.PAYMENT_CHANGE_TO} ${counterPartyNames}`
      : CASHBOOK_DESCRIPTIONS.PAYMENT_MODE_CHANGED;

    await addShiftPayment({
      amount: isCredit ? diff : 0,
      debit: !isCredit ? Math.abs(diff) : 0,
      description,
      local_id: Number(pm.id),
      name: pm.name,
      sale_bill_no: sale.bill_no,
      sale_local_id: sale.local_id,
      shift_id: Number(shiftId),
      time: time,
      type: CASHBOOK_ENTRY_TYPE.PAYMENT_MODE_CHANGE,
    });
  }
}

async function logDebitPaymentModeChanges({ debits, creditModeNames, sale, shiftId, time, type }, totalCreditCapacity) {
  let remainingCapacity = totalCreditCapacity;
  for (const { pm, diff } of debits) {
    let remainingDebit = Math.abs(diff);

    if (remainingCapacity > 0) {
      const swapAmt = Math.min(remainingDebit, remainingCapacity);
      const description = creditModeNames
        ? `${CASHBOOK_DESCRIPTIONS.PAYMENT_CHANGE_TO} ${creditModeNames}`
        : CASHBOOK_DESCRIPTIONS.PAYMENT_MODE_CHANGED;

      await addShiftPayment({
        amount: 0,
        debit: swapAmt,
        description,
        local_id: Number(pm.id),
        name: pm.name,
        sale_bill_no: sale.bill_no,
        sale_local_id: sale.local_id,
        shift_id: Number(shiftId),
        time,
        type: CASHBOOK_ENTRY_TYPE.PAYMENT_MODE_CHANGE,
      });
      remainingDebit -= swapAmt;
      remainingCapacity -= swapAmt;
    }

    if (remainingDebit > 0) {
      await addShiftPayment({
        amount: 0,
        debit: remainingDebit,
        description: type === CASHBOOK_ENTRY_TYPE.SALE ? "" : CASHBOOK_DESCRIPTIONS.SALE_EDIT,
        local_id: Number(pm.id),
        name: pm.name,
        sale_bill_no: sale.bill_no,
        sale_local_id: sale.local_id,
        shift_id: Number(shiftId),
        time,
        type,
      });
    }
  }
}

async function logCreditPaymentModeChanges({ credits, debitModeNames, sale, shiftId, time, type }, totalDebitCapacity) {
  let remainingCapacity = totalDebitCapacity;
  for (const { pm, diff } of credits) {
    let remainingCredit = diff;

    if (remainingCapacity > 0) {
      const swapAmt = Math.min(remainingCredit, remainingCapacity);
      const description = debitModeNames
        ? `${CASHBOOK_DESCRIPTIONS.PAYMENT_CHANGE_FROM} ${debitModeNames}`
        : CASHBOOK_DESCRIPTIONS.PAYMENT_MODE_CHANGED;

      await addShiftPayment({
        amount: swapAmt,
        debit: 0,
        description,
        local_id: Number(pm.id),
        name: pm.name,
        sale_bill_no: sale.bill_no,
        sale_local_id: sale.local_id,
        shift_id: Number(shiftId),
        time,
        type: CASHBOOK_ENTRY_TYPE.PAYMENT_MODE_CHANGE,
      });
      remainingCredit -= swapAmt;
      remainingCapacity -= swapAmt;
    }

    if (remainingCredit > 0) {
      await addShiftPayment({
        amount: remainingCredit,
        debit: 0,
        description: type === CASHBOOK_ENTRY_TYPE.SALE ? "" : CASHBOOK_DESCRIPTIONS.SALE_EDIT,
        local_id: Number(pm.id),
        name: pm.name,
        sale_bill_no: sale.bill_no,
        sale_local_id: sale.local_id,
        shift_id: Number(shiftId),
        time,
        type,
      });
    }
  }
}

async function logPaymentModeFullChanges({
  debits,
  credits,
  debitModeNames,
  creditModeNames,
  sale,
  shiftId,
  time,
  type,
}) {
  const totalCreditCapacity = credits.reduce((sum, c) => sum + c.diff, 0);
  const totalDebitCapacity = debits.reduce((sum, d) => sum + Math.abs(d.diff), 0);

  await logDebitPaymentModeChanges({ debits, creditModeNames, sale, shiftId, time, type }, totalCreditCapacity);
  await logCreditPaymentModeChanges({ credits, debitModeNames, sale, shiftId, time, type }, totalDebitCapacity);
}

export const logPaymentModeChange = async ({ paymentModes, sale, shiftId, isEditingPaymentModeOnly, type = "" }) => {
  const time = DateTimeHelpers.getLocalISOString();
  const diffs = paymentModes
    .map((pm) => ({
      pm,
      diff: (Number(pm.amount) || 0) - (Number(pm.initialAmount) || 0),
    }))
    .filter((d) => d.diff !== 0);

  const debits = diffs.filter((d) => d.diff < 0);
  const credits = diffs.filter((d) => d.diff > 0);

  const debitModeNames = debits.map((d) => d.pm.name).join(", ");
  const creditModeNames = credits.map((d) => d.pm.name).join(", ");

  if (isEditingPaymentModeOnly) {
    await logPaymentModeEditsOnly({ diffs, debitModeNames, creditModeNames, sale, shiftId, time });
  } else {
    await logPaymentModeFullChanges({ debits, credits, debitModeNames, creditModeNames, sale, shiftId, time, type });
  }
};

export function isShiftClosing() {
  return localStorage.getItem(STORAGE_KEYS.SHIFT_STATUS_KEY) === SHIFT_STATUS.CLOSING;
}

export function getShiftClosingEnteredBalances() {
  const str = localStorage.getItem(STORAGE_KEYS.SHIFT_ENTERED_BALANCES);
  if (!str) return null;
  try {
    return JSON.parse(str);
  } catch (e) {
    return null;
  }
}

export function setShiftClosingState(enteredBalances) {
  localStorage.setItem(STORAGE_KEYS.SHIFT_STATUS_KEY, SHIFT_STATUS.CLOSING);
  localStorage.setItem(STORAGE_KEYS.SHIFT_ENTERED_BALANCES, JSON.stringify(enteredBalances));
}

export function clearShiftClosingState() {
  localStorage.removeItem(STORAGE_KEYS.SHIFT_STATUS_KEY);
  localStorage.removeItem(STORAGE_KEYS.SHIFT_ENTERED_BALANCES);
}

export async function performShiftCreation(payload, tenders, startTime) {
  let newShiftId;
  let newShiftName;
  let syncStatus = SHIFT_SYNC_STATUS.SYNCED;

  try {
    const resp = await ShiftService.createShift(payload);
    newShiftId = resp.data.id;
    newShiftName = resp.data.name;
  } catch (err) {
    const savedShifts = JSON.parse(localStorage.getItem(STORAGE_KEYS.ALL_SHIFTS) || "[]");
    const lastShift = savedShifts.length > 0 ? savedShifts[savedShifts.length - 1] : null;
    newShiftId = lastShift ? Number(lastShift.id) + 1 : 1;
    newShiftName = payload.name;
    syncStatus = SHIFT_SYNC_STATUS.PENDING_CREATE;
  }

  const dbEntries = tenders
    .filter((tender) => Number(tender.amount) > 0)
    .map((tender) => ({
      shift_id: newShiftId,
      local_id: tender.payment_mode.id,
      name: tender.payment_mode.name,
      opening: Number(tender.amount) || 0,
      amount: 0,
      debit: 0,
      type: CASHBOOK_ENTRY_TYPE.OPENING_BALANCE,
      time: DateTimeHelpers.getLocalISOString(),
    }));

  if (dbEntries.length > 0) {
    await Promise.all(dbEntries.map((entry) => addShiftPayment(entry)));
  }

  const newShift = {
    id: newShiftId,
    name: newShiftName,
    date: DateTimeHelpers.convertDateToIsoDMY(new Date()),
    opened_at: startTime,
    sync_status: syncStatus,
    tenders: payload.tenders,
    shift: payload.shift,
    cashier: payload.cashier || SalespersonHelper.getCurrentSalesperson(),
  };
  const savedShifts = JSON.parse(localStorage.getItem(STORAGE_KEYS.ALL_SHIFTS) || "[]");
  savedShifts.push(newShift);
  localStorage.setItem(STORAGE_KEYS.ALL_SHIFTS, JSON.stringify(savedShifts));

  localStorage.setItem(STORAGE_KEYS.SHIFT_ID, newShiftId);
  localStorage.setItem(STORAGE_KEYS.SHIFT, newShiftName);
  clearShiftExpirySkipTime();
}

export async function finalizeShiftClosureState(shiftId, payload, apiTenders, syncStatus, clearAllData) {
  const allShifts = JSON.parse(localStorage.getItem(STORAGE_KEYS.ALL_SHIFTS) || "[]");
  const updatedShifts = allShifts.map((s) => {
    if (Number(s.id) === Number(shiftId)) {
      const finalSyncStatus =
        s.sync_status === SHIFT_SYNC_STATUS.PENDING_CREATE
          ? SHIFT_SYNC_STATUS.PENDING_CREATE
          : syncStatus || s.sync_status;
      return {
        ...s,
        closed_at: payload.closed_at,
        sync_status: finalSyncStatus,
        closing_tenders: apiTenders,
        close_payload: payload,
      };
    }
    return s;
  });
  localStorage.setItem(STORAGE_KEYS.ALL_SHIFTS, JSON.stringify(updatedShifts));
  localStorage.removeItem(STORAGE_KEYS.SHIFT_ID);
  localStorage.removeItem(STORAGE_KEYS.SHIFT);
  clearShiftClosingState();

  if (SalespersonHelper.isSalespersonLoginRequired()) {
    SalespersonHelper.clearSalespersonSession();
  }

  if (clearAllData) {
    await clearAllIndexedDB();
    window.location.reload();
    return true;
  }
  await SalesHelper.clearSyncedSales();
  return false;
}

export function getCurrentActiveShift() {
  const shiftId = localStorage.getItem(STORAGE_KEYS.SHIFT_ID);
  if (!shiftId) return null;
  const allShifts = JSON.parse(localStorage.getItem(STORAGE_KEYS.ALL_SHIFTS) || "[]");
  return allShifts.find((s) => Number(s.id) === Number(shiftId) && !s.closed_at) || null;
}

export function isCurrentShiftCashierSame(currentShift, salesPerson) {
  if (!currentShift || !currentShift.cashier || !salesPerson) return true;
  return Number(currentShift.cashier.id) === Number(salesPerson.id);
}

function calculateStockDamageTotal(stockDamages = []) {
  return (stockDamages || []).reduce((sum, d) => sum + (Number(d.payable) || 0), 0);
}

function calculatePurchaseTotal(purchases = []) {
  return (purchases || []).reduce((sum, p) => {
    const itemsTotal = (p.items || []).reduce((iSum, item) => iSum + Number(item.total || item.payable || 0), 0);
    const pTotal = Number(p.totalPurchaseAmount || p.totalPurachseAmount || p.totalAmount || itemsTotal || 0);
    return sum + pTotal;
  }, 0);
}

export async function getPurchasesByShift(shiftId) {
  try {
    const allPurchases = (await db.getAll(COLLECTIONS.PURCHASES.name)) || [];
    const targetShiftId = shiftId != null ? Number(shiftId) : null;

    let shiftStartTime = null;
    if (targetShiftId) {
      const allShifts = JSON.parse(localStorage.getItem(STORAGE_KEYS.ALL_SHIFTS) || "[]");
      const targetShift = allShifts.find((s) => Number(s.id) === targetShiftId);
      if (targetShift?.opened_at) {
        shiftStartTime = new Date(targetShift.opened_at);
      }
    }

    return allPurchases.filter((item) => {
      if (item.status === DRAFT_STATUS.DRAFT) return false;

      if (targetShiftId) {
        const itemShiftId =
          item.shift?.id != null ? Number(item.shift.id) : item.shift_id != null ? Number(item.shift_id) : null;
        if (itemShiftId != null) {
          return itemShiftId === targetShiftId;
        }
        if (shiftStartTime && (item.purchaseDate || item.date)) {
          return new Date(item.purchaseDate || item.date) >= shiftStartTime;
        }
      }
      return true;
    });
  } catch (err) {
    console.error("Failed to get purchases by shift:", err);
    return [];
  }
}

export async function getStockDamagesByShift(shiftId) {
  try {
    const allDamages = (await db.getAll(COLLECTIONS.STOCK_DAMAGES.name)) || [];
    const targetShiftId = shiftId != null ? Number(shiftId) : null;

    let shiftStartTime = null;
    if (targetShiftId) {
      const allShifts = JSON.parse(localStorage.getItem(STORAGE_KEYS.ALL_SHIFTS) || "[]");
      const targetShift = allShifts.find((s) => Number(s.id) === targetShiftId);
      if (targetShift?.opened_at) {
        shiftStartTime = new Date(targetShift.opened_at);
      }
    }

    return allDamages.filter((item) => {
      if (item.status === DRAFT_STATUS.DRAFT) return false;

      if (targetShiftId) {
        if (item.shift_id != null) {
          return Number(item.shift_id) === targetShiftId;
        }
        if (shiftStartTime && item.date) {
          return new Date(item.date) >= shiftStartTime;
        }
      }
      return true;
    });
  } catch (err) {
    console.error("Failed to get stock damages by shift:", err);
    return [];
  }
}
